// Pagination utility to generate page numbers with ellipsis
export interface PaginationItem {
  type: 'page' | 'ellipsis';
  page?: number;
  isActive?: boolean;
}

export function generatePaginationItems(
  currentPage: number,
  totalPages: number,
  maxVisiblePages: number = 5
): PaginationItem[] {
  if (totalPages <= maxVisiblePages) {
    // If total pages is less than max visible, show all pages
    return Array.from({ length: totalPages }, (_, i) => ({
      type: 'page' as const,
      page: i + 1,
      isActive: currentPage === i + 1,
    }));
  }

  const items: PaginationItem[] = [];
  const sidePages = Math.floor((maxVisiblePages - 1) / 2);

  // Always show first page
  items.push({
    type: 'page',
    page: 1,
    isActive: currentPage === 1,
  });

  let startPage: number;
  let endPage: number;

  if (currentPage <= sidePages + 1) {
    // Near the beginning
    startPage = 2;
    endPage = Math.min(maxVisiblePages - 1, totalPages - 1);
  } else if (currentPage >= totalPages - sidePages) {
    // Near the end
    startPage = Math.max(totalPages - maxVisiblePages + 2, 2);
    endPage = totalPages - 1;
  } else {
    // In the middle
    startPage = currentPage - sidePages + 1;
    endPage = currentPage + sidePages - 1;
  }

  // Add ellipsis after first page if needed
  if (startPage > 2) {
    items.push({ type: 'ellipsis' });
  }

  // Add middle pages
  for (let i = startPage; i <= endPage; i++) {
    items.push({
      type: 'page',
      page: i,
      isActive: currentPage === i,
    });
  }

  // Add ellipsis before last page if needed
  if (endPage < totalPages - 1) {
    items.push({ type: 'ellipsis' });
  }

  // Always show last page if there are more than 1 pages
  if (totalPages > 1) {
    items.push({
      type: 'page',
      page: totalPages,
      isActive: currentPage === totalPages,
    });
  }

  return items;
}